import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { isEqual } from 'lodash';

import { useEventEmitter, useUtils } from '@ezbobdev/ui-commons';
import { MainContext, useTranslation } from '@ezbobdev/ui-framework';

const deriveFreeSoloInputValue = (val, options) => {
  const matched = options.find((option) => option?.value === val);
  if (matched) return matched.label ?? '';
  return val !== null && val !== undefined ? String(val) : '';
};

export const useAutocomplete = (props, widgetName = 'Autocomplete') => {
  const { t, i18n } = useTranslation();
  const { MUIAutocompleteProps, onInputChange, onChange, onBlur, startAdornment, inputValue: inputValueProp } = props;

  const { actions } = useContext(MainContext);
  const { emitEvent } = useEventEmitter();
  const { configByName, getConfig } = useUtils(props, widgetName);

  const className = getConfig('className');
  const values = getConfig('values', []);
  const variant = getConfig('variant');
  const disabled = getConfig(`disabled`, false);
  const readOnly = getConfig(`readOnly`, false);
  const freeSolo = getConfig('freeSolo', false);
  const hasTooltip = getConfig('hasTooltip');
  const maxSelectedOptions = getConfig('maxSelectedOptions', Number.MAX_SAFE_INTEGER);
  const multiple = getConfig('multiple', false);
  const matchInsideWords = getConfig('matchInsideWords', false);
  const isRequired = getConfig('isRequired', false);
  const privacyRules = getConfig('privacyRules', {
    input: 'mask-user-input',
    option: 'allow',
  });
  const renderTags = getConfig('renderTags', true);
  const tooltipColor =
    getConfig('adornment.color') ||
    actions.settings.getUserConfigProp('widgetsConfig.globals.MaterialTooltip.iconColor');

  const caseValue = actions.case.getCaseValue(configByName);

  const [value, setValue] = useState(multiple ? [] : null);
  const [localInputValue, setLocalInputValue] = useState(() =>
    freeSolo ? deriveFreeSoloInputValue(caseValue, []) : '',
  );

  // Controlled dropdown open state — needed for iOS VoiceOver a11y fixes.
  // We ignore MUI's "blur" close reason entirely to avoid closing during iOS 3-finger
  // scroll (which can trigger phantom blurs on the input). Desktop click-outside is
  // handled separately by a mousedown listener on the document.
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const autocompleteWrapperRef = useRef(null);

  // Close on click outside (desktop). Uses mousedown so it fires before the input's
  // blur event. Touch interactions don't fire mousedown reliably, so this won't
  // close the dropdown during iOS scroll. The listbox is rendered in a Popper that's
  // a sibling of the autocomplete root (even with disablePortal) — we look it up via
  // aria-controls and treat clicks inside it as "inside" so option selection works.
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (event) => {
      const wrapper = autocompleteWrapperRef.current;
      if (!wrapper) return;
      if (wrapper.contains(event.target)) return;
      const input = wrapper.querySelector('input[role="combobox"]');
      const listboxId = input?.getAttribute('aria-controls');
      if (listboxId) {
        const listbox = document.getElementById(listboxId);
        if (listbox?.contains(event.target)) return;
      }
      setIsDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const openDropdown = useCallback(() => setIsDropdownOpen(true), []);

  const closeDropdown = useCallback((event, reason) => {
    // Ignore "blur" — handled by the mousedown listener above so we don't fight
    // iOS scroll gestures that fire phantom blur events.
    if (reason === 'blur') return;
    setIsDropdownOpen(false);
  }, []);

  const clearText = i18n.exists('globals.clearText') ? t('globals.clearText') : 'Clear';
  const openText = i18n.exists('globals.openText') ? t('globals.openText') : 'Open';
  const closeText = i18n.exists('globals.closeText') ? t('globals.closeText') : 'Close';

  const noOptionsTextPath = { configByName: `${configByName}.noOptionsText`, global: 'globals.noOptionsText' };
  const getTranslation = (path) => i18n.exists(path) && t(path);

  const noOptionsText =
    props.noOptionsText ||
    getTranslation(noOptionsTextPath.configByName) ||
    getTranslation(noOptionsTextPath.global) ||
    undefined;
  // Assumes tha onInputChange is passed only when options come form an API
  const isServerSide = !!onInputChange;

  const options = useMemo(() => {
    return values.map((item) => {
      if (typeof item === 'string') {
        const labelKey = `${configByName}.values.${item}`;
        return {
          label: i18n.exists(labelKey) ? t(labelKey) : item,
          value: item,
        };
      }

      const { label, value, ...rest } = item;

      const labelKey = `${configByName}.values.${value}`;
      return {
        label: label?.toString() || (i18n.exists(labelKey) ? t(labelKey) : value?.toString()),
        value,
        ...rest,
      };
    });
  }, [configByName, values]);

  const handleInputChange = useCallback(
    (e, inputVal, reason) => {
      let valueToStore = inputVal;
      if (reason === 'clear') valueToStore = '';
      // MUI fires reason='reset' when the `value` prop changes. In freeSolo mode, applying
      // that would revert the user's in-flight typing to a stale label and re-trigger
      // updateInCase in a feedback loop. Forward 'reset' only to external onInputChange
      // listeners (server-side search), but do not touch our own state or write back.
      if (freeSolo && reason !== 'reset') setLocalInputValue(valueToStore);
      if (typeof onInputChange === 'function') onInputChange(e, valueToStore, reason);
      if (typeof onChange === 'function' && freeSolo && reason !== 'reset') onChange(valueToStore);
    },
    [freeSolo, onInputChange, onChange],
  );

  const handleChange = useCallback(
    (e, option, reason) => {
      let valueToStore = option?.value;
      if (multiple && option.length > maxSelectedOptions) return;
      if (multiple) valueToStore = option.map((item) => item?.value || item);
      if (reason === 'clear') valueToStore = '';
      if (freeSolo && !multiple) setLocalInputValue(option?.label ?? option ?? '');
      if (typeof onChange === 'function') onChange(valueToStore);

      // Mobile screen reader a11y: TalkBack/VoiceOver focus sits on the option element
      // when the user double-taps to select. MUI's internal inputRef.focus() fires
      // *before* the option unmounts, so the screen reader ignores it; then the option
      // is removed and TalkBack drops accessibility focus to <body>. Deferring focus to
      // the next tick (after the option is gone) moves both DOM and screen-reader focus
      // back to the input, which then announces the newly-selected value.
      if (reason === 'selectOption' && !multiple) {
        setTimeout(() => {
          const input = autocompleteWrapperRef.current?.querySelector('input[role="combobox"]');
          input?.focus();
        }, 0);
      }
    },
    [freeSolo, multiple, maxSelectedOptions, onChange],
  );

  const handleBlur = useCallback(
    (e) => {
      // TODO: Check why onBlur is commented out, check validation behaviour
      emitEvent(e.type, { widgetName: configByName });
      // if (!multiple && e.target.value !== (value?.label ?? value)) onBlur(value);
    },
    [value, multiple, onBlur],
  );

  useEffect(() => {
    const newValue = () => {
      if (multiple) {
        if (!caseValue) return [];
        return caseValue
          .map((val) => {
            const matchedOption = options.find((option) => option.value === val);
            return matchedOption ? matchedOption : freeSolo ? val : null;
          })
          .filter((_) => _);
      }
      const matchedOption = options.find((option) => option?.value === caseValue);
      return matchedOption ? matchedOption : freeSolo ? caseValue : null;
    };

    const oldValue = value;

    // Deep comparison
    if (!isEqual(oldValue, newValue())) {
      setValue(newValue());
    }

    // freeSolo-only: keep local inputValue in sync with external caseValue changes.
    // Self-inflicted debounce settles produce an equal derived value — the guard makes them no-ops.
    if (freeSolo && !multiple && inputValueProp === undefined) {
      const nextInputValue = deriveFreeSoloInputValue(caseValue, options);
      if (nextInputValue !== localInputValue) setLocalInputValue(nextInputValue);
    }
  }, [multiple, freeSolo, caseValue, options]);

  return {
    configByName,
    className,
    isServerSide,
    freeSolo,
    multiple,
    variant,
    options,
    value,
    noOptionsText,
    matchInsideWords,
    privacyRules,
    MUIAutocompleteProps,
    handleInputChange,
    handleChange,
    handleBlur,
    hasTooltip,
    isRequired,
    renderTags,
    t,
    clearText,
    openText,
    closeText,
    startAdornment,
    inputValue: inputValueProp !== undefined ? inputValueProp : freeSolo ? localInputValue : undefined,
    disabled,
    readOnly,
    tooltipColor,
    isDropdownOpen,
    openDropdown,
    closeDropdown,
    autocompleteWrapperRef,
  };
};
